import React, { useEffect, useRef } from 'react';
import {
  TextInput,
  Text,
  View,
  StyleSheet,
  TextInputProps,
  Animated,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Colors, Fonts, Radii, ControlHeight, Shadows, Typography } from '../constants';

interface InputProps extends Omit<TextInputProps, 'style'> {
  label?: string;
  error?: string;
  helperText?: string; // Helper text shown below input (e.g., hints, requirements)
  /** Ionicon on the left inside the field (mail, lock, …) */
  icon?: keyof typeof Ionicons.glyphMap;
  style?: any; // Container style override (to match Select pattern)
  inputStyle?: any; // TextInput style override
  labelStyle?: any; // Label style override
}

/**
 * Text field (design v2): 48px, control radius (6), hairline border, accent border
 * + focus ring on focus, error ink on error, optional leading icon.
 */
export const Input = React.forwardRef<TextInput, InputProps>(({
  label,
  error,
  helperText,
  icon,
  style,
  inputStyle,
  labelStyle,
  onFocus,
  onBlur,
  ...props
}, ref) => {
  const [focused, setFocused] = React.useState(false);
  // Animation values for slide, fade, and height
  const slideAnim = useRef(new Animated.Value(0)).current;
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const heightAnim = useRef(new Animated.Value(0)).current;
  const [shouldRender, setShouldRender] = React.useState(false);
  const [displayError, setDisplayError] = React.useState('');

  useEffect(() => {
    if (error) {
      // Show the error container and update text
      setDisplayError(error);
      setShouldRender(true);

      // Slide in, fade in, and expand height
      Animated.parallel([
        Animated.timing(slideAnim, {
          toValue: 1,
          duration: 200,
          useNativeDriver: true,
        }),
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: 200,
          useNativeDriver: true,
        }),
        Animated.timing(heightAnim, {
          toValue: 1,
          duration: 200,
          useNativeDriver: false, // Height animation cannot use native driver
        }),
      ]).start();
    } else if (shouldRender) {
      // Slide out, fade out, and collapse height
      Animated.parallel([
        Animated.timing(slideAnim, {
          toValue: 0,
          duration: 150,
          useNativeDriver: true,
        }),
        Animated.timing(fadeAnim, {
          toValue: 0,
          duration: 150,
          useNativeDriver: true,
        }),
        Animated.timing(heightAnim, {
          toValue: 0,
          duration: 150,
          useNativeDriver: false, // Height animation cannot use native driver
        }),
      ]).start(() => {
        // After animation completes, hide the container
        setShouldRender(false);
        setDisplayError('');
      });
    }
  }, [error, slideAnim, fadeAnim, heightAnim, shouldRender]);

  // Interpolate values for smooth animation
  const translateY = slideAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [-10, 0], // Slide from -10px to 0px
  });

  const maxHeight = heightAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [0, 20], // Animate from 0 to ~20px (approximate error text height)
  });

  return (
    <View style={[styles.container, style]}>
      {!!label && <Text style={[styles.label, labelStyle]}>{label}</Text>}
      <View>
        {icon && !props.multiline ? (
          <Ionicons name={icon} size={20} color={Colors.textMedium} style={styles.icon} pointerEvents="none" />
        ) : null}
        <TextInput
          ref={ref}
          style={[
            styles.input,
            !!props.multiline && styles.inputMultiline,
            !!icon && !props.multiline && styles.inputWithIcon,
            focused && styles.inputFocused,
            !!error && styles.inputError,
            Platform.OS === 'web' && ({ outlineStyle: 'none' } as any),
            inputStyle,
          ]}
          placeholderTextColor={Colors.textLight}
          onFocus={(e) => { setFocused(true); onFocus?.(e); }}
          onBlur={(e) => { setFocused(false); onBlur?.(e); }}
          {...props}
        />
      </View>
      {!!helperText && !error && (
        <Text style={styles.helperText}>{helperText}</Text>
      )}
      {shouldRender && (
        <Animated.View
          style={{
            maxHeight,
            overflow: 'hidden',
          }}
        >
          <Animated.View
            style={{
              opacity: fadeAnim,
              transform: [{ translateY }],
            }}
          >
            <Text style={styles.errorText}>{displayError}</Text>
          </Animated.View>
        </Animated.View>
      )}
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    marginBottom: 16,
  },
  label: {
    ...Typography.label,
    color: Colors.textDark,
    marginBottom: 8,
  },
  icon: {
    position: 'absolute',
    left: 14,
    top: (ControlHeight.md - 20) / 2,
    zIndex: 1,
  },
  input: {
    borderWidth: 1,
    borderColor: Colors.borderLight,
    borderRadius: Radii.control,
    paddingHorizontal: 16,
    height: ControlHeight.md, // Fixed height for consistent alignment across platforms
    fontFamily: Fonts.regular,
    fontSize: 15,
    backgroundColor: Colors.white,
    color: Colors.text,
    textAlignVertical: 'center', // Center placeholder and text vertically on Android
  },
  inputMultiline: {
    height: undefined,
    minHeight: 100,
    paddingVertical: 12,
    textAlignVertical: 'top',
  },
  inputWithIcon: {
    paddingLeft: 44,
  },
  inputFocused: {
    borderColor: Colors.primary,
    ...(Platform.OS === 'web' && Shadows.focusRing),
  },
  inputError: {
    borderColor: Colors.error,
  },
  errorText: {
    ...Typography.meta,
    color: Colors.error,
    marginTop: 4,
  },
  helperText: {
    ...Typography.meta,
    color: Colors.textLight,
    marginTop: 4,
  },
});
